import { useCallback } from "react";
import { useSelector } from "react-redux";
import { Booking } from "../../../../../../types/bookings";
import { convertToDayjs, findSelectOption, isSelectOption } from "../../../../../libs/utils";
import { no_of_installments } from "../../utils";
import { rentTypeOptions } from "../AddPlanComm";
import { TOR_PERIOD_OPTIONS } from "./constants";

type RentTypeValue = (typeof rentTypeOptions)[number]["value"];

const normalizePlanSelectOptions = (plan: Record<string, unknown>) => {
  const inst = plan.installments;
  if (isSelectOption(inst)) {
    const raw = inst.value;
    const num = typeof raw === "number" && !Number.isNaN(raw) ? raw : Number(raw);
    if (!Number.isNaN(num)) {
      const found = no_of_installments.find((o) => o.value === num);
      if (found) plan.installments = found;
    }
  }

  const rt = plan.rentType;
  if (isSelectOption(rt)) {
    const v = rt.value;
    if (typeof v === "string") {
      const found = findSelectOption(rentTypeOptions, v as RentTypeValue);
      if (found) plan.rentType = found;
    }
  }

  const tor = plan.torPeriod;
  if (isSelectOption(tor)) {
    const raw = tor.value;
    const num = typeof raw === "number" && !Number.isNaN(raw) ? raw : Number(raw);
    if (!Number.isNaN(num)) {
      const found = TOR_PERIOD_OPTIONS.find((o) => o.value === num);
      if (found) plan.torPeriod = found;
    }
  }
};

type BuildCopyPlanParams = {
  previousPlan: unknown;
  yearNumber: number;
  commencementDate?: string;
};

export const useBuildPlanFromPreviousYear = () => {
  const { bookingDetails } = useSelector((state: any) => state?.newBooking);
  const { leaseTerms } = (bookingDetails ?? {}) as Booking;

  const buildPlanFromPreviousYear = useCallback(
    (params: BuildCopyPlanParams): Record<string, unknown> | null => {
      const { previousPlan, yearNumber } = params;
      if (previousPlan == null || typeof previousPlan !== "object") return null;

      const payload = structuredClone(previousPlan) as Record<string, unknown>;
      delete payload.id;

      for (const key of Object.keys(payload)) {
        if (key.startsWith("old")) delete payload[key];
      }

      delete payload.securityDeposit;
      delete payload.escalateRentPercent;

      payload.billingScheduleRows = undefined;
      payload.serviceChargeSchedule = undefined;

      payload.noOfYear = yearNumber;
      payload.rentCommencementDate = convertToDayjs(leaseTerms?.commencementDate);

      normalizePlanSelectOptions(payload);

      if (payload?.serviceCharge != null && typeof payload?.serviceCharge === "object") {
        (payload.serviceCharge as Record<string, unknown>).rentCommencementDate = convertToDayjs(
          leaseTerms?.commencementDate,
        );
      }

      return payload;
    },
    [leaseTerms?.commencementDate],
  );

  return { buildPlanFromPreviousYear };
};
